// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useCallback, useEffect, useState } from "react";
import {
  Plus,
  SquarePen,
  Trash2,
  Loader2,
  X,
  MapPin,
  Map as MapIcon,
  LayoutGrid,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import toast from "react-hot-toast";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import SortableHeader from "../../components/admin/SortableHeader";
import PageLoader from "../../components/PageLoader";
import tenantService from "../../services/tenantService";
import locationService from "../../services/locationService";
import { formatErrorMessage } from "../../utils/apiHelper";
import { useRoutes } from "../../hooks/useRoutes";
import { hasSuperAdminAccess } from "../../config/roleConfig";
import useSortableData from "../../hooks/useSortableData";
import LocationFormFields from "../../components/admin/LocationFormFields";
import {
  operatingHoursToState,
  operatingHoursFromState,
} from "../../components/admin/OperatingHoursEditor";
import {
  DEFAULT_TIMEZONE,
  DEFAULT_COUNTRY,
} from "../../utils/locationFormHelpers";

const TENANT_ROLES = ["owner", "operator", "partner", "viewer"];

// Only "Status" needs a custom sort value (the is_active boolean); the other
// columns map directly to row fields.
const LOCATION_OVERVIEW_SORT_ACCESSORS = {
  status: (l) => (l.is_active ? 1 : 0),
};

// Baseline form state for the location modal. Mirrors the columns of
// vhp_location so the admin (Super Admin) can edit every field — the
// previous slim shape (name/city/address/status only) didn't match the
// editor's permissions.
const getInitialFormData = () => ({
  name: "",
  vhp_code: "",
  slug: "",
  description: "",
  description_html: "",
  latitude: "",
  longitude: "",
  timezone: DEFAULT_TIMEZONE,
  country: DEFAULT_COUNTRY,
  address_street: "",
  address_city: "",
  address_postal_code: "",
  operating_hours: operatingHoursToState(null),
  is_active: true,
  is_public: true,
});

const StandorteListView = () => {
  // Per the spec, only super-admins can assign locations to tenants.
  // Tenant-admins can view but not mutate the tenant ↔ location join.
  const { userRoles } = useRoutes();
  const isSuperAdmin = hasSuperAdminAccess(userRoles || []);

  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  // M:N tenant assignment state. `original` is loaded when the edit modal
  // opens; on save we diff against `assigned` to produce the POST/DELETE
  // list. Each row is { tenant_id, role, is_primary }.
  const [assignedTenants, setAssignedTenants] = useState([]);
  const [originalAssignments, setOriginalAssignments] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [overviewSummary, setOverviewSummary] = useState({
    total_locations: 0,
    active_locations: 0,
    total_scenarios: 0,
    total_spaces: 0,
  });
  const [selectedMandant, setSelectedMandant] = useState("all");
  const [mandanten, setMandanten] = useState([]);
  const [mandantenLoading, setMandantenLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  // Inline error for the create/edit form modal.
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadMandanten = async () => {
      setMandantenLoading(true);
      try {
        const tenants = await tenantService.getAllTenantsForFilter({
          includeAll: false,
          limit: 500,
        });
        if (!cancelled) setMandanten(Array.isArray(tenants) ? tenants : []);
      } finally {
        if (!cancelled) setMandantenLoading(false);
      }
    };
    loadMandanten();
    return () => {
      cancelled = true;
    };
  }, []);

  // Refetch helper, also called after create/update/delete.
  const loadLocations = useCallback(async () => {
    setLocationsLoading(true);
    try {
      const response = await locationService.getLocationsOverview({
        page: 1,
        limit: 25,
      });
      const items = Array.isArray(response?.items) ? response.items : [];
      setLocations(
        items.map((location) => ({
          id: location.id,
          name: location.name ?? "—",
          mandant_id: location.tenant_id ?? "",
          mandant_name: location.tenant_name ?? "—",
          city: location.city ?? "—",
          address: location.address ?? "—",
          szenarien_count: location.scenarios_count ?? 0,
          spaces_count: location.spaces_count ?? 0,
          flaechen_count: location.spaces_count ?? 0,
          is_active: Boolean(location.is_active),
        })),
      );
      setOverviewSummary({
        total_locations: response?.summary?.total_locations ?? items.length,
        active_locations:
          response?.summary?.active_locations ??
          items.filter((l) => l.is_active).length,
        total_scenarios: response?.summary?.total_scenarios ?? 0,
        total_spaces: response?.summary?.total_spaces ?? 0,
      });
    } catch (error) {
      console.error("Error loading locations overview:", error);
      setLocations([]);
      setOverviewSummary({
        total_locations: 0,
        active_locations: 0,
        total_scenarios: 0,
        total_spaces: 0,
      });
    } finally {
      setLocationsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const filteredLocations =
    selectedMandant === "all"
      ? locations
      : locations.filter((l) => l.mandant_id === selectedMandant);

  const {
    sortedItems: sortedLocations,
    sortConfig,
    requestSort,
  } = useSortableData(filteredLocations, LOCATION_OVERVIEW_SORT_ACCESSORS);

  const [formData, setFormData] = useState(getInitialFormData());
  // `slugDirty` stops the slug auto-syncing from the name once the admin
  // has manually edited it.
  const [slugDirty, setSlugDirty] = useState(false);

  const openCreate = () => {
    setFormError(null);
    setFormData(getInitialFormData());
    setSlugDirty(false);
    // Fresh location has no current assignments. If the parent page already
    // has a Mandant filter selected, pre-seed that tenant as a convenience.
    const seed =
      selectedMandant && selectedMandant !== "all"
        ? [{ tenant_id: selectedMandant, role: "operator", is_primary: true }]
        : [];
    setAssignedTenants(seed);
    setOriginalAssignments([]);
    setEditingLocation(null);
    setShowCreateModal(true);
  };

  const openEdit = async (loc) => {
    setFormError(null);
    setEditingLocation(loc);
    // The overview row carries only a slim subset of columns. Fetch the
    // full Location so the admin sees and can edit every field.
    setFormData(getInitialFormData());
    setSlugDirty(false);
    setAssignedTenants([]);
    setOriginalAssignments([]);
    setTenantsLoading(true);
    try {
      const [fullLoc, assignments] = await Promise.all([
        locationService.getLocationById(loc.id).catch(() => null),
        locationService.getLocationTenants(loc.id, { tenants: mandanten }),
      ]);
      const src = fullLoc ?? loc;
      setFormData({
        name: src.name ?? "",
        vhp_code: src.vhp_code?.trim() ?? "",
        slug: src.slug ?? "",
        description: src.description ?? "",
        description_html: src.description_html ?? "",
        latitude: src.latitude != null ? String(src.latitude) : "",
        longitude: src.longitude != null ? String(src.longitude) : "",
        timezone: src.timezone || DEFAULT_TIMEZONE,
        country: src.country || DEFAULT_COUNTRY,
        address_street: src.address_street ?? "",
        address_city: src.address_city ?? "",
        address_postal_code: src.address_postal_code ?? "",
        operating_hours: operatingHoursToState(src.operating_hours),
        is_active: src.is_active ?? true,
        is_public: src.is_public ?? true,
      });
      // Existing slug → treat as user-set so a name edit doesn't overwrite it.
      setSlugDirty(Boolean(src.slug?.trim()));
      setAssignedTenants(assignments);
      setOriginalAssignments(assignments);
    } catch (err) {
      console.error("Could not load location / tenant assignments:", err);
    } finally {
      setTenantsLoading(false);
    }
  };

  const closeEdit = () => {
    setFormError(null);
    setEditingLocation(null);
    setFormData(getInitialFormData());
    setSlugDirty(false);
  };

  // Build the API payload from the form. Super-admin can edit every column
  // exposed in LocationFormFields. Tenant assignments travel separately via
  // the tenant_locations M:N flow further down.
  const buildLocationPayload = () => ({
    name: formData.name.trim(),
    vhp_code: formData.vhp_code?.trim() || null,
    slug: formData.slug?.trim() || null,
    description: formData.description?.trim() || null,
    description_html: formData.description_html?.trim() || null,
    latitude: formData.latitude ? parseFloat(formData.latitude) : null,
    longitude: formData.longitude ? parseFloat(formData.longitude) : null,
    timezone: formData.timezone?.trim() || null,
    country: formData.country?.trim() || null,
    address_street: formData.address_street?.trim() || null,
    address_city: formData.address_city?.trim() || null,
    address_postal_code: formData.address_postal_code?.trim() || null,
    operating_hours: operatingHoursFromState(formData.operating_hours),
    is_active: Boolean(formData.is_active),
    is_public: Boolean(formData.is_public),
  });

  // Diff the working `assignedTenants` against what was loaded from the
  // backend in `originalAssignments` and apply the delta:
  //  - new tenant_id → POST  /admin/tenants/{tid}/locations
  //  - role or is_primary changed → PUT /admin/tenants/{tid}/locations/{lid}
  //  - removed tenant → DELETE /admin/tenants/{tid}/locations/{lid}
  // Errors are collected; first error is surfaced so the admin sees which
  // tenant failed.
  const syncTenantAssignments = async (locationId) => {
    if (!isSuperAdmin) return;
    const originalById = new Map(
      originalAssignments.map((a) => [String(a.tenant_id), a]),
    );
    const desiredById = new Map(
      assignedTenants.map((a) => [String(a.tenant_id), a]),
    );
    const errors = [];

    for (const [tid, desired] of desiredById) {
      const prev = originalById.get(tid);
      const payload = {
        location_id: locationId,
        role: desired.role || "operator",
        is_primary: !!desired.is_primary,
      };
      try {
        if (!prev) {
          await tenantService.assignLocationToTenant(tid, payload);
        } else if (
          prev.role !== payload.role ||
          !!prev.is_primary !== payload.is_primary
        ) {
          await tenantService.updateTenantLocation(tid, locationId, payload);
        }
      } catch (e) {
        errors.push({ tid, op: prev ? "update" : "assign", error: e });
      }
    }

    for (const [tid] of originalById) {
      if (!desiredById.has(tid)) {
        try {
          await tenantService.removeLocationFromTenant(tid, locationId);
        } catch (e) {
          errors.push({ tid, op: "remove", error: e });
        }
      }
    }

    if (errors.length > 0) {
      const first = errors[0];
      const message = formatErrorMessage(first.error);
      toast.error(
        `Mandant-Zuweisung fehlgeschlagen (${errors.length}): ${message}`,
        { duration: 8000 },
      );
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) return;

    if (assignedTenants.length === 0) {
      setFormError(
        "Mindestens ein Mandant muss zugewiesen sein, bevor der Standort erstellt werden kann.",
      );
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const created = await locationService.createLocation(
        buildLocationPayload(),
      );
      const newId = created?.id ?? created?.data?.id;
      if (newId && assignedTenants.length > 0) {
        await syncTenantAssignments(newId);
      }
      toast.success(`Standort „${formData.name.trim()}" wurde erstellt.`);
      await loadLocations();
      setShowCreateModal(false);
      setAssignedTenants([]);
      setOriginalAssignments([]);
      setFormData(getInitialFormData());
      setSlugDirty(false);
    } catch (err) {
      setFormError(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim() || !editingLocation) return;
    setSaving(true);
    setFormError(null);
    try {
      await locationService.updateLocation(
        editingLocation.id,
        buildLocationPayload(),
      );
      await syncTenantAssignments(editingLocation.id);
      toast.success(`Standort „${formData.name.trim()}" wurde aktualisiert.`);
      await loadLocations();
      closeEdit();
    } catch (err) {
      setFormError(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (loc) => {
    setDeleteError(null);
    setDeleteConfirm(loc);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    setSaving(true);
    setDeleteError(null);
    try {
      await locationService.deleteLocation(deleteConfirm.id);
      // Backend confirmed deletion → drop the row locally and close.
      setLocations((prev) => prev.filter((l) => l.id !== deleteConfirm.id));
      toast.success(
        `Standort „${deleteConfirm.name ?? "Unbekannt"}“ wurde gelöscht.`,
      );
      setDeleteConfirm(null);
    } catch (err) {
      // 409 from the backend's safety checks (linked scenarios / tenant
      // assignments) — keep the dialog open and surface the message inline
      // so the admin can act on it (instead of dismissing the toast).
      const status = err?.response?.status;
      const message = formatErrorMessage(err);
      if (status === 409) {
        setDeleteError(message);
      } else {
        toast.error(message);
        setDeleteConfirm(null);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative">
      <GlobalAdminHeader
        title="Standorte"
        subtitle="Standorte definieren, die zu Mandanten gehören"
        buttons={[
          {
            text: "Standort anlegen",
            icon: <Plus className="h-4 w-4" />,
            onClick: openCreate,
            className:
              "flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-color text-white font-medium shadow-lg hover:opacity-90 transition-opacity",
          },
        ]}
      />

      <div className="mx-auto px-4 lg:px-4 py-4 text-left dark:text-slate-200 space-y-6">
        {/* Metric cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative p-5 rounded-2xl bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl  border-primary-color overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:shadow-[0px_12px_40px_rgba(99,102,241,0.15)]">
            <div className="absolute inset-0 bg-gradient-primary-secondary-tint opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Standorte
                </p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold primary-color">
                    {overviewSummary.total_locations}
                  </span>
                </div>
              </div>
              <div className="p-2.5 rounded-xl bg-primary-color-tint primary-color">
                <MapPin className="w-5 h-5" />
              </div>
            </div>
          </div>
          <div className="relative p-5 rounded-2xl bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl border-primary-color overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:shadow-[0px_12px_40px_rgba(99,102,241,0.15)]">
            <div className="absolute inset-0 bg-gradient-primary-secondary-tint opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Aktiv
                </p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold primary-color">
                    {overviewSummary.active_locations}
                  </span>
                </div>
              </div>
              <div className="p-2.5 rounded-xl bg-primary-color-tint primary-color">
                <CheckCircle className="w-5 h-5" />
              </div>
            </div>
          </div>
          <div className="relative p-5 rounded-2xl bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl border-primary-color overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:shadow-[0px_12px_40px_rgba(99,102,241,0.15)]">
            <div className="absolute inset-0 bg-gradient-primary-secondary-tint opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Szenarien
                </p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold primary-color">
                    {overviewSummary.total_scenarios}
                  </span>
                </div>
              </div>
              <div className="p-2.5 rounded-xl bg-primary-color-tint primary-color">
                <MapIcon className="w-5 h-5" />
              </div>
            </div>
          </div>
          <div className="relative p-5 rounded-2xl bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl border-primary-color overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:shadow-[0px_12px_40px_rgba(99,102,241,0.15)]">
            <div className="absolute inset-0 bg-gradient-primary-secondary-tint opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Flächen
                </p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold primary-color">
                    {overviewSummary.total_spaces}
                  </span>
                </div>
              </div>
              <div className="p-2.5 rounded-xl bg-primary-color-tint primary-color">
                <LayoutGrid className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>

        {/* Mandant filter */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm p-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 dark:text-slate-300">
              Mandant:
            </label>
            <select
              value={selectedMandant}
              onChange={(e) => setSelectedMandant(e.target.value)}
              className="px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-slate-200 text-sm min-w-[200px] focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="all">Alle Mandanten</option>
              {mandantenLoading && mandanten.length === 0 ? (
                <option value="" disabled>
                  Lädt…
                </option>
              ) : null}
              {mandanten.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50">
                  <SortableHeader
                    label="Standort"
                    sortKey="name"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Mandant"
                    sortKey="mandant_name"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Stadt"
                    sortKey="city"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Adresse"
                    sortKey="address"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Szenarien"
                    sortKey="szenarien_count"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Flächen"
                    sortKey="flaechen_count"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Status"
                    sortKey="status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <th className="text-right text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                {locationsLoading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-8 text-center text-gray-500 dark:text-slate-400 text-sm"
                    >
                      <PageLoader message="Lade Standorte..." />
                    </td>
                  </tr>
                ) : filteredLocations.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-8 text-center text-gray-500 dark:text-slate-400 text-sm"
                    >
                      Keine Standorte gefunden.
                    </td>
                  </tr>
                ) : (
                  sortedLocations.map((loc) => (
                    <tr
                      key={loc.id}
                      className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-slate-100">
                        {loc.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300">
                        {loc.mandant_name ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300">
                        {loc.city}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300">
                        {loc.address}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300">
                        {loc.szenarien_count ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300">
                        {loc.flaechen_count ?? loc.spaces_count ?? "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white ${loc.is_active ? "bg-green-500/90 dark:bg-green-600" : "bg-gray-400 dark:bg-slate-500"}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${loc.is_active ? "bg-white" : "bg-gray-200"}`}
                          />
                          {loc.is_active ? "Aktiv" : "Inaktiv"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => openEdit(loc)}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-400 transition-colors"
                            title="Bearbeiten"
                          >
                            <SquarePen className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(loc)}
                            className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500 dark:text-red-400 transition-colors"
                            title="Löschen"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => !saving && setShowCreateModal(false)}
            aria-hidden
          />
          <div
            className="relative w-full max-w-4xl max-h-[92vh] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col dark:border dark:border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100">
                Neuer Standort
              </h2>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                disabled={saving}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-500 dark:text-slate-400 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form
              onSubmit={handleCreateSubmit}
              className="flex-1 overflow-y-auto p-6 space-y-4"
            >
              {formError && (
                <div className="flex gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-sm text-red-700 dark:text-red-300">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{formError}</span>
                </div>
              )}
              {/* Tenant ↔ location is M:N per spec. Super-admin only.
                  Tenant-admins see read-only assignments. */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Mandanten <span className="text-red-500">*</span>{" "}
                  {!isSuperAdmin && (
                    <span className="text-xs text-gray-400">
                      (nur Super-Admin)
                    </span>
                  )}
                </label>
                {tenantsLoading ? (
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    Lade Zuweisungen…
                  </p>
                ) : (
                  <div className="space-y-2">
                    {assignedTenants.length === 0 ? (
                      <p className="text-xs text-gray-500 dark:text-slate-400">
                        Keine Mandanten zugewiesen.
                      </p>
                    ) : (
                      assignedTenants.map((a) => {
                        const tenantMeta = mandanten.find(
                          (m) => String(m.id) === String(a.tenant_id),
                        );
                        return (
                          <div
                            key={a.tenant_id}
                            className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600"
                          >
                            <span className="text-sm font-medium text-gray-900 dark:text-slate-100 flex-1 min-w-[120px]">
                              {tenantMeta?.name ||
                                a.tenant_name ||
                                `#${a.tenant_id}`}
                            </span>
                            <select
                              value={a.role || "operator"}
                              disabled={!isSuperAdmin}
                              onChange={(e) =>
                                setAssignedTenants((prev) =>
                                  prev.map((x) =>
                                    x.tenant_id === a.tenant_id
                                      ? { ...x, role: e.target.value }
                                      : x,
                                  ),
                                )
                              }
                              className="px-2 py-1 text-xs border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded disabled:opacity-60"
                            >
                              {TENANT_ROLES.map((r) => (
                                <option key={r} value={r}>
                                  {r}
                                </option>
                              ))}
                            </select>
                            <label className="inline-flex items-center gap-1 text-xs text-gray-700 dark:text-slate-300">
                              <input
                                type="checkbox"
                                disabled={!isSuperAdmin}
                                checked={!!a.is_primary}
                                onChange={(e) =>
                                  setAssignedTenants((prev) =>
                                    prev.map((x) =>
                                      x.tenant_id === a.tenant_id
                                        ? {
                                            ...x,
                                            is_primary: e.target.checked,
                                          }
                                        : x,
                                    ),
                                  )
                                }
                              />
                              primär
                            </label>
                            {isSuperAdmin && (
                              <button
                                type="button"
                                onClick={() =>
                                  setAssignedTenants((prev) =>
                                    prev.filter(
                                      (x) => x.tenant_id !== a.tenant_id,
                                    ),
                                  )
                                }
                                className="p-1 text-red-500 hover:text-red-700"
                                title="Mandant entfernen"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        );
                      })
                    )}
                    {isSuperAdmin && (
                      <select
                        value=""
                        onChange={(e) => {
                          const tid = e.target.value;
                          if (!tid) return;
                          if (
                            assignedTenants.some(
                              (a) => String(a.tenant_id) === String(tid),
                            )
                          )
                            return;
                          setAssignedTenants((prev) => [
                            ...prev,
                            {
                              tenant_id: tid,
                              role: "operator",
                              is_primary: prev.length === 0,
                            },
                          ]);
                          e.target.value = "";
                        }}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                      >
                        <option value="">+ Mandant hinzufügen…</option>
                        {mandanten
                          .filter(
                            (m) =>
                              !assignedTenants.some(
                                (a) => String(a.tenant_id) === String(m.id),
                              ),
                          )
                          .map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name}
                            </option>
                          ))}
                      </select>
                    )}
                  </div>
                )}
              </div>
              <LocationFormFields
                formData={formData}
                setFormData={setFormData}
                slugDirty={slugDirty}
                setSlugDirty={setSlugDirty}
                slotBeforeFlags={
                  editingLocation && (
                    // Server-derived counts (scenario + space joins); shown
                    // read-only just above the Aktiv/Öffentlich flags.
                    <div className="grid grid-cols-2 gap-4 px-3 py-2 rounded-lg bg-gray-50 dark:bg-slate-700/50 text-xs text-gray-600 dark:text-slate-300">
                      <div>
                        <span className="block uppercase tracking-wider text-gray-400 dark:text-slate-500">
                          Szenarien
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-slate-100">
                          {editingLocation.szenarien_count ?? 0}
                        </span>
                      </div>
                      <div>
                        <span className="block uppercase tracking-wider text-gray-400 dark:text-slate-500">
                          Flächen
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-slate-100">
                          {editingLocation.flaechen_count ??
                            editingLocation.spaces_count ??
                            0}
                        </span>
                      </div>
                    </div>
                  )
                }
              />
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  disabled={saving}
                  className="px-4 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2.5 rounded-xl bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Anlegen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editingLocation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => !saving && closeEdit()}
            aria-hidden
          />
          <div
            className="relative w-full max-w-4xl max-h-[92vh] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col dark:border dark:border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative px-6 py-4 border-b border-gray-100 dark:border-slate-700 shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/5 via-cyan-500/5 to-emerald-500/5 dark:from-indigo-500/10 dark:via-cyan-500/10 dark:to-emerald-500/10" />
              <div className="relative flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100">
                    Standort bearbeiten
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                    {editingLocation.name}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeEdit}
                  disabled={saving}
                  className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-500 dark:text-slate-400 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <form
              onSubmit={handleEditSubmit}
              className="flex-1 overflow-y-auto p-6 space-y-4"
            >
              {formError && (
                <div className="flex gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-sm text-red-700 dark:text-red-300">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{formError}</span>
                </div>
              )}
              {/* Mandanten — M:N multi-select (super-admin only). Same
                  widget as the create modal; shared `assignedTenants` state
                  drives both. */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Mandanten{" "}
                  {!isSuperAdmin && (
                    <span className="text-xs text-gray-400">
                      (nur Super-Admin)
                    </span>
                  )}
                </label>
                {tenantsLoading ? (
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    Lade Zuweisungen…
                  </p>
                ) : (
                  <div className="space-y-2">
                    {assignedTenants.length === 0 ? (
                      <p className="text-xs text-gray-500 dark:text-slate-400">
                        Keine Mandanten zugewiesen.
                      </p>
                    ) : (
                      assignedTenants.map((a) => {
                        const tenantMeta = mandanten.find(
                          (m) => String(m.id) === String(a.tenant_id),
                        );
                        return (
                          <div
                            key={a.tenant_id}
                            className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600"
                          >
                            <span className="text-sm font-medium text-gray-900 dark:text-slate-100 flex-1 min-w-[120px]">
                              {tenantMeta?.name ||
                                a.tenant_name ||
                                `#${a.tenant_id}`}
                            </span>
                            <select
                              value={a.role || "operator"}
                              disabled={!isSuperAdmin}
                              onChange={(e) =>
                                setAssignedTenants((prev) =>
                                  prev.map((x) =>
                                    x.tenant_id === a.tenant_id
                                      ? { ...x, role: e.target.value }
                                      : x,
                                  ),
                                )
                              }
                              className="px-2 py-1 text-xs border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded disabled:opacity-60"
                            >
                              {TENANT_ROLES.map((r) => (
                                <option key={r} value={r}>
                                  {r}
                                </option>
                              ))}
                            </select>
                            <label className="inline-flex items-center gap-1 text-xs text-gray-700 dark:text-slate-300">
                              <input
                                type="checkbox"
                                disabled={!isSuperAdmin}
                                checked={!!a.is_primary}
                                onChange={(e) =>
                                  setAssignedTenants((prev) =>
                                    prev.map((x) =>
                                      x.tenant_id === a.tenant_id
                                        ? {
                                            ...x,
                                            is_primary: e.target.checked,
                                          }
                                        : x,
                                    ),
                                  )
                                }
                              />
                              primär
                            </label>
                            {isSuperAdmin && (
                              <button
                                type="button"
                                onClick={() =>
                                  setAssignedTenants((prev) =>
                                    prev.filter(
                                      (x) => x.tenant_id !== a.tenant_id,
                                    ),
                                  )
                                }
                                className="p-1 text-red-500 hover:text-red-700"
                                title="Mandant entfernen"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        );
                      })
                    )}
                    {isSuperAdmin && (
                      <select
                        value=""
                        onChange={(e) => {
                          const tid = e.target.value;
                          if (!tid) return;
                          if (
                            assignedTenants.some(
                              (a) => String(a.tenant_id) === String(tid),
                            )
                          )
                            return;
                          setAssignedTenants((prev) => [
                            ...prev,
                            {
                              tenant_id: tid,
                              role: "operator",
                              is_primary: prev.length === 0,
                            },
                          ]);
                          e.target.value = "";
                        }}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                      >
                        <option value="">+ Mandant hinzufügen…</option>
                        {mandanten
                          .filter(
                            (m) =>
                              !assignedTenants.some(
                                (a) => String(a.tenant_id) === String(m.id),
                              ),
                          )
                          .map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name}
                            </option>
                          ))}
                      </select>
                    )}
                  </div>
                )}
              </div>
              <LocationFormFields
                formData={formData}
                setFormData={setFormData}
                slugDirty={slugDirty}
                setSlugDirty={setSlugDirty}
                slotBeforeFlags={
                  editingLocation && (
                    // Server-derived counts (scenario + space joins); shown
                    // read-only just above the Aktiv/Öffentlich flags.
                    <div className="grid grid-cols-2 gap-4 px-3 py-2 rounded-lg bg-gray-50 dark:bg-slate-700/50 text-xs text-gray-600 dark:text-slate-300">
                      <div>
                        <span className="block uppercase tracking-wider text-gray-400 dark:text-slate-500">
                          Szenarien
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-slate-100">
                          {editingLocation.szenarien_count ?? 0}
                        </span>
                      </div>
                      <div>
                        <span className="block uppercase tracking-wider text-gray-400 dark:text-slate-500">
                          Flächen
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-slate-100">
                          {editingLocation.flaechen_count ??
                            editingLocation.spaces_count ??
                            0}
                        </span>
                      </div>
                    </div>
                  )
                }
              />
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeEdit}
                  disabled={saving}
                  className="px-4 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2.5 rounded-xl bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => {
              if (saving) return;
              setDeleteConfirm(null);
              setDeleteError(null);
            }}
            aria-hidden
          />
          <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl dark:border dark:border-slate-700 max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-1">
                  Standort löschen?
                </h3>
                <p className="text-sm text-gray-600 dark:text-slate-400 mb-1">
                  „{deleteConfirm.name ?? "Unbekannt"}“ wird dauerhaft gelöscht.
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
              </div>
            </div>
            {/* Inline error for 409 conflicts: linked scenarios or tenant
                assignments. Backend message tells the admin what to remove
                first. */}
            {deleteError && (
              <div className="mt-4 flex gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-sm text-red-700 dark:text-red-300">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{deleteError}</span>
              </div>
            )}
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setDeleteConfirm(null);
                  setDeleteError(null);
                }}
                disabled={saving}
                className="px-5 py-2.5 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 font-medium transition-colors disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={saving}
                className="px-5 py-2.5 rounded-xl bg-red-600 dark:bg-red-500 text-white font-medium hover:bg-red-700 dark:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Trash2 className="w-5 h-5" />
                )}
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StandorteListView;
